import { Button, FullScreenLoading } from '@civilgrid/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { getOrganization as apiGetOrganization, joinOrgAsAdmin as apiJoinOrg } from '~/api/internal';
import { ORG_SWITCHER_ORGANIZATIONS_KEY } from '~/common/queryKeys';
import { useQuery as useSearchParams } from '~/common/routerUtils';
import type { OrganizationDetail } from '~/common/types';
import Input from '~/components2/forms/Input';
import { Notify } from '~/components2/Notify';
import OrganizationPicker from '~/components2/OrganizationPicker';
import store from '~/store';
import { MajorHeading } from '../common/Heading';

export default function JoinOrgAsAdmin() {
  const search = useSearchParams();
  const organizationIdFromUrl = search.get('organizationId');
  const redirectUri = search.get('redirectUri');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useSnapshot(store);

  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationDetail | null>(null);

  // Check if user is already a member of the selected organization
  const isAlreadyMember = selectedOrganization && me?.organizations?.some((org) => org.id === selectedOrganization.id);

  const query = useQuery({
    enabled: !!organizationIdFromUrl,
    queryKey: ['join-org-as-admin', { organizationId: organizationIdFromUrl }],
    queryFn: () => apiGetOrganization(organizationIdFromUrl!),
  });

  // Set selected organization from URL parameter when query data is available
  useEffect(() => {
    if (query.data && !selectedOrganization) {
      setSelectedOrganization(query.data);
    }
  }, [query.data, selectedOrganization]);

  const handleOrganizationSelect = (organizationId: string, organization: OrganizationDetail) => {
    setSelectedOrganization(organization);
  };

  const mutation = useMutation({
    mutationKey: ['join-org-as-admin', { organizationId: selectedOrganization?.id }],
    mutationFn: (orgId: string) => {
      // Check if already a member before making the API call
      if (isAlreadyMember) {
        throw new Error('Already a member of this organization');
      }
      return apiJoinOrg(orgId);
    },
    onSuccess: () => {
      Notify.info({
        message: `Successfully joined org ${selectedOrganization?.name}`,
      });
      if (redirectUri) {
        navigate({ to: redirectUri });
      }

      queryClient.invalidateQueries({ queryKey: ORG_SWITCHER_ORGANIZATIONS_KEY });
      store.refreshMe();
    },
    onError: (error: any) => {
      if (error.message === 'Already a member of this organization') {
        Notify.info({
          message: `You are already a member of ${selectedOrganization?.name}`,
        });
      } else {
        Notify.error({
          message: `An error occurred joining the org ${selectedOrganization?.name}`,
        });
      }
    },
  });

  const isFetching = query.isFetching || mutation.isPending;

  if (isFetching && organizationIdFromUrl) return <FullScreenLoading />;

  return (
    <div className="flex w-full flex-col items-start space-y-4">
      <div className="space-y-2">
        <MajorHeading>Select Organization</MajorHeading>
        <OrganizationPicker
          selectedOrganization={selectedOrganization}
          onOrganizationSelect={handleOrganizationSelect}
        />
      </div>

      {selectedOrganization && (
        <>
          <Input
            label="Organization ID"
            value={selectedOrganization.id}
            disabled
            canCopy
            error={mutation?.isError ? 'Error joining Org' : undefined}
          />

          <Input
            label="Organization Name"
            value={selectedOrganization.name}
            disabled
            canCopy
            error={mutation?.isError ? 'Error joining Org' : undefined}
          />

          {isAlreadyMember ? (
            <div className="flex items-center space-x-2 rounded-md p-3 border border-green-200">
              <div className="text-green-600 text-sm">✓ You are already a member of this organization</div>
            </div>
          ) : (
            <Button
              onClick={() => mutation.mutate(selectedOrganization.id)}
              disabled={isFetching || !selectedOrganization}
              loading={isFetching}
            >
              Join {selectedOrganization.name}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
